import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Fade,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import axios from "axios";
import FormDetailsCard from "../components/FormDetails";
import PDFFormEditor from "../editor/PDFFormEditor";
import {
  acquireFormEditLock,
  formatFormEditLockedMessage,
  isFormEditLockLostError,
  releaseFormEditLock as releaseFormEditLockRequest,
  renewFormEditLock,
} from "../editor/formEditLockApi";
import { getEditorInstanceLabel } from "../../../../../utils/fieldSettings";

const normalizeEditorFields = (fields = []) =>
  (Array.isArray(fields) ? fields : []).map((f) => ({
    ...f,
    x: Number(f.x ?? f.rect?.X ?? f.rect?.x ?? 0),
    y: Number(f.y ?? f.rect?.Y ?? f.rect?.y ?? 0),
    width: Number(f.width ?? f.rect?.W ?? f.rect?.width ?? 0),
    height: Number(f.height ?? f.rect?.H ?? f.rect?.height ?? 0),
    page: Number(f.page ?? f.page_num ?? f.pageIndex ?? 1),
    page_num: Number(f.page_num ?? f.page ?? f.pageIndex ?? 1),
    rect: {
      W: Number(f.rect?.W ?? f.width ?? f.rect?.width ?? 0),
      H: Number(f.rect?.H ?? f.height ?? f.rect?.height ?? 0),
      X: Number(f.rect?.X ?? f.x ?? f.rect?.x ?? 0),
      Y: Number(f.rect?.Y ?? f.y ?? f.rect?.y ?? 0),
      S: Array.isArray(f.rect?.S)
        ? f.rect.S.map((v) => Number(v)).filter((v) => Number.isFinite(v))
        : [],
    },
    field_label: getEditorInstanceLabel(f),
    field_required: Boolean(f.field_required),
  }));

const NewForm = ({ onError, onSuccess, currentSession }) => {
  const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1GB
  const SIMULATED_PROCESSING_DURATION_MS = 50000;
  const SIMULATED_PROGRESS_TICK_MS = 400;
  const PROCESSING_MESSAGES = [
    "Uploading PDF securely...",
    "Validating document structure...",
    "Extracting embedded PDF text...",
    "Scanning pages for form fields...",
    "Detecting checkboxes, lines, and signature zones...",
    "Grouping related regions and labels...",
    "Matching labels to possible field names...",
    "Preparing editor data for review...",
  ];

  const [uploading, setUploading] = useState(false);
  const [finalizingUpload, setFinalizingUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalStep, setDetailsModalStep] = useState("processing");
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [messageVisible, setMessageVisible] = useState(true);
  const [guessedFormDetails, setGuessedFormDetails] = useState(null);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [isProcessingDelayed, setIsProcessingDelayed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const fetchOptionsRef = useRef(null);
  const currentEditLockRef = useRef(null);
  const editorSavePromiseRef = useRef(null);
  const handleVerifiedDetailsChange = useCallback(() => {
    setUploadError("");
  }, []);

  useEffect(() => {
    if (!uploading || detailsModalStep !== "processing") {
      setProcessingMessageIndex(0);
      setMessageVisible(true);
      return undefined;
    }

    const interval = setInterval(() => {
      setMessageVisible(false);

      setTimeout(() => {
        setProcessingMessageIndex(
          (prev) => (prev + 1) % PROCESSING_MESSAGES.length,
        );
        setMessageVisible(true);
      }, 250);
    }, 3800);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading, detailsModalStep]);

  useEffect(() => {
    if (!uploading || detailsModalStep !== "processing") {
      setSimulatedProgress(0);
      setIsProcessingDelayed(false);
      return undefined;
    }

    const totalTicks = Math.max(
      1,
      Math.floor(SIMULATED_PROCESSING_DURATION_MS / SIMULATED_PROGRESS_TICK_MS),
    );

    const interval = setInterval(() => {
      setSimulatedProgress((prev) => {
        const next = prev + 100 / totalTicks;
        if (next >= 99) {
          setIsProcessingDelayed(true);
        }
        return Math.min(next, 99);
      });
    }, SIMULATED_PROGRESS_TICK_MS);

    return () => clearInterval(interval);
  }, [uploading, detailsModalStep]);

  useEffect(() => {
    const fetchJurisdictionOptions = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/jurisdictions/florida/counties-municipalities`,
          { currentSession },
          { withCredentials: true },
        );

        if (response?.data?.success) {
          setJurisdictionOptions(response.data.data || []);
        } else {
          onError("Failed to load counties and municipalities.");
        }
      } catch (error) {
        console.error("Failed to fetch jurisdiction options:", error);
        onError("Failed to load counties and municipalities.");
      }
    };

    if (!fetchOptionsRef?.current) {
      fetchOptionsRef.current = true;
      fetchJurisdictionOptions();
    }
  }, [currentSession, onError]);

  const releaseEditLock = useCallback(
    async (lock = currentEditLockRef.current) => {
      if (!lock?.formId || !lock?.lockId) return;
      if (
        currentEditLockRef.current?.formId === lock.formId &&
        currentEditLockRef.current?.lockId === lock.lockId
      ) {
        currentEditLockRef.current = null;
      }
      try {
        await releaseFormEditLockRequest({
          currentSession,
          formId: lock.formId,
          lockId: lock.lockId,
        });
      } catch (error) {
        if (error?.response?.status !== 409) {
          console.error("Failed to release form editor lock:", error);
        }
      }
    },
    [currentSession],
  );

  const recoverEditLock = useCallback(async () => {
    const formId =
      currentEditLockRef.current?.formId ||
      formData?.formId ||
      formData?.form?.form_id ||
      "";
    if (!formId) return false;

    const recoveredLock = await acquireFormEditLock({ currentSession, formId });
    currentEditLockRef.current = recoveredLock;
    setFormData((prev) => ({ ...(prev || {}), editLock: recoveredLock }));
    return recoveredLock;
  }, [currentSession, formData?.form?.form_id, formData?.formId]);

  // Handle PDF editor save
  const handlePdfEditorSave = useCallback(
    async (updatedFormData, saveOptions = {}) => {
      const isBackgroundSave = saveOptions?.background === true;
      const isBackgroundSaveCurrent = () =>
        !saveOptions?.signal?.aborted &&
        (typeof saveOptions?.isCurrent !== "function" ||
          saveOptions.isCurrent());

      // Skip autosave while another save is pending. Manual saves wait for
      // earlier writes so an older autosave cannot overwrite a newer save.
      const previousSave = editorSavePromiseRef.current;
      if (
        isBackgroundSave &&
        (previousSave || !formData?.formId || !isBackgroundSaveCurrent())
      ) {
        return false;
      }

      let finishSave;
      const pendingSave = new Promise((resolve) => {
        finishSave = resolve;
      });
      editorSavePromiseRef.current = pendingSave;

      try {
        if (previousSave) await previousSave;
        if (!isBackgroundSave) setLoading(true);

        let currentLock = currentEditLockRef.current;
        if (
          isBackgroundSave &&
          (!isBackgroundSaveCurrent() ||
            !currentLock?.lockId ||
            currentLock.formId !== formData?.formId)
        ) {
          return false;
        }

        if (currentLock?.formId && currentLock?.lockId) {
          try {
            await renewFormEditLock({
              currentSession,
              formId: currentLock.formId,
              lockId: currentLock.lockId,
            });
          } catch (error) {
            // Autosave must not recover locks by replacing formData or show
            // errors. Existing heartbeat/manual-save recovery stays intact.
            if (isBackgroundSave) return false;

            if (isFormEditLockLostError(error)) {
              try {
                currentLock = await recoverEditLock();
              } catch (recoveryError) {
                onError(
                  recoveryError?.response?.status === 409
                    ? formatFormEditLockedMessage(recoveryError)
                    : recoveryError?.response?.data?.message ||
                        "The edit lock could not be recovered. Your changes remain in the editor; try saving again.",
                );
                return false;
              }
            } else {
              onError(
                "The edit session could not be confirmed because of a connection problem. Your changes remain in the editor; check your connection and save again.",
              );
              return false;
            }
          }
        }

        const {
          title = updatedFormData?.guessedFormDetails?.label_guess ||
            updatedFormData?.formName ||
            updatedFormData?.fileName ||
            "",
          isClose = false,
          silent = false,
        } = saveOptions || {};

        if (
          isBackgroundSave &&
          (!isBackgroundSaveCurrent() ||
            currentEditLockRef.current?.formId !== currentLock?.formId ||
            currentEditLockRef.current?.lockId !== currentLock?.lockId)
        ) {
          return false;
        }

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/save-form-data`,
          {
            form_id: formData?.formId,
            formLabel: title,
            lockId: currentLock?.lockId || formData?.editLock?.lockId || "",
            instances: updatedFormData?.formFields?.map((f) => {
              const blockId = f?.block_id ? f.block_id.split("__sub")[0] : "";
              if (f?.field_instance_id) {
                return { ...f, block_id: blockId };
              } else {
                return {
                  ...f,
                  form_id: formData?.formId,
                  page_num: f?.page,
                  rect: {
                    W: f?.width,
                    H: f?.height,
                    X: f?.x,
                    Y: f?.y,
                  },
                  field_label: getEditorInstanceLabel(f),
                  block_id: blockId,
                };
              }
            }),
          },
          {
            withCredentials: true,
            ...(isBackgroundSave
              ? { signal: saveOptions?.signal, timeout: 60000 }
              : {}),
          },
        );

        // Do not touch loading, notifications, editor state, or the lock
        // after a background response, including one for a closed editor.
        if (isBackgroundSave) {
          return isBackgroundSaveCurrent() && response?.data?.success === true;
        }

        if (response.data.success) {
          if (!silent) {
            onSuccess("Form updated successfully");
          }
          if (isClose) {
            await releaseEditLock();
            window.dispatchEvent(new Event("forceHideSideNavOff"));
            window.dispatchEvent(new Event("forceHideAppChromeOff"));
            setPdfEditorOpen(false);
            setFormData(null);
          }
          return true;
        } else {
          onError("Failed to save form changes");
          return false;
        }
      } catch (error) {
        if (isBackgroundSave) return false;

        console.error("Error saving form data:", error);
        onError("Error saving form changes");
        return false;
      } finally {
        if (!isBackgroundSave) setLoading(false);
        if (editorSavePromiseRef.current === pendingSave) {
          editorSavePromiseRef.current = null;
        }
        finishSave();
      }
    },
    [
      currentSession,
      formData,
      onError,
      onSuccess,
      recoverEditLock,
      releaseEditLock,
    ],
  );

  const handleDiscardConfirmed = async () => {
    await releaseEditLock();
    setSelectedFile(null);
    setUploading(false);
    setFinalizingUpload(false);
    setDetailsModalOpen(false);
    setDetailsModalStep("processing");
    setConfirmDiscardOpen(false);
    setFormData(null);
    setGuessedFormDetails(null);
    setSimulatedProgress(0);
    setIsProcessingDelayed(false);
    setUploadError("");
    setPdfEditorOpen(false);

    window.dispatchEvent(new Event("forceHideSideNavOff"));
    window.dispatchEvent(new Event("forceHideAppChromeOff"));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePdfEditorClose = (hasUnsavedChanges = false) => {
    if (hasUnsavedChanges) {
      setConfirmDiscardOpen(true);
      return;
    }

    return handleDiscardConfirmed();
  };

  const processSelectedForm = async (file) => {
    if (!file) {
      return;
    }

    setUploading(true);
    setDetailsModalOpen(true);
    setDetailsModalStep("processing");
    setProcessingMessageIndex(0);
    setMessageVisible(true);
    setSimulatedProgress(0);
    setIsProcessingDelayed(false);
    setUploadError("");

    try {
      const data = new FormData();
      data.append("file", file);
      data.append("currentSession", currentSession);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/process`,
        data,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!response?.data?.success) {
        onError(response?.data?.message || "Form processing failed");
        return;
      }

      setSimulatedProgress(100);
      setIsProcessingDelayed(false);

      const analysis = response?.data?.data?.analysis || null;
      const processedPayload = response?.data?.data || null;

      setFormData((prev) => ({
        ...(prev || {}),
        processedData: processedPayload,
      }));

      if (analysis) {
        const nextGuessedFormDetails = {
          label_guess: analysis.label_guess || file.name,
          state: analysis.state || "fl",
          isStatewideForm:
            typeof analysis.is_statewide_form === "boolean"
              ? analysis.is_statewide_form
              : true,
          county: analysis.county || "",
          jurisdictionLevel: analysis.jurisdiction_level || "county",
          municipality: analysis.municipality || "",
          mun_type: analysis.mun_type || "",
          isNoc: typeof analysis.is_noc === "boolean" ? analysis.is_noc : false,
        };

        setGuessedFormDetails(nextGuessedFormDetails);
        setFormData((prev) =>
          prev
            ? {
                ...prev,
                guessedFormDetails: nextGuessedFormDetails,
              }
            : {
                guessedFormDetails: nextGuessedFormDetails,
              },
        );
      }

      setDetailsModalStep("details");
    } catch (error) {
      console.error("Form processing error:", error);
      onError("Form processing failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isPdfMime = file.type === "application/pdf";
    const hasPdfExtension = file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfMime && !hasPdfExtension) {
      onError("Only PDF files are allowed.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError("File is too large. Maximum allowed size is 1GB.");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setUploadError("");

    setFormData({
      formName: file.name,
      fileName: file.name,
      formFields: [],
      skipPdfInit: true,
    });

    window.dispatchEvent(new Event("closeSideNav"));
    window.dispatchEvent(new Event("forceHideSideNavOn"));
    window.dispatchEvent(new Event("forceHideAppChromeOn"));
    setPdfEditorOpen(true);

    await processSelectedForm(file);
  };

  const handleRemoveSelectedFile = () => {
    setConfirmDiscardOpen(true);
  };

  const handleUpload = async (formDetails) => {
    setUploadError("");

    if (!selectedFile) {
      setUploadError("Please select a file to upload.");
      return;
    }

    const processedData = formData?.processedData;

    if (!processedData) {
      setUploadError(
        "Processed form data is missing. Please cancel and re-process the PDF.",
      );
      return;
    }

    setFinalizingUpload(true);

    try {
      const payload = {
        currentSession,
        processed_data: processedData,
        verified_details: {
          ...formDetails,
          file_name: selectedFile.name,
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/upload`,
        payload,
        {
          withCredentials: true,
        },
      );

      if (!response?.data?.success) {
        setUploadError(response?.data?.message || "Form upload failed.");
        return;
      }

      const uploadedData = response?.data?.data || {};
      const uploadedFields = normalizeEditorFields(
        uploadedData.formFields ?? uploadedData.form_field_instances,
      );
      const uploadedForm = uploadedData.form || {};
      const formId = uploadedForm.form_id || uploadedData.form_id || "";
      const formStatus = String(
        uploadedForm.status || uploadedData.status || "",
      ).toLowerCase();

      let pdfFile = null;
      if (formId) {
        try {
          const pdfRes = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/forms/download`,
            { formId, downloadType: "pdf" },
            { withCredentials: true, responseType: "blob" },
          );
          pdfFile = new File([pdfRes.data], `${formId}.pdf`, {
            type: "application/pdf",
          });
        } catch (e) {
          console.error("Uploaded PDF fetch failed:", e);
        }
      }

      let acquiredLock = null;
      if (formStatus === "inactive") {
        try {
          acquiredLock = await acquireFormEditLock({ currentSession, formId });
          currentEditLockRef.current = acquiredLock;
        } catch (error) {
          const message =
            error?.response?.status === 409
              ? formatFormEditLockedMessage(error)
              : error?.response?.data?.message ||
                "The uploaded form could not be opened for editing.";
          setUploadError(message);
          onError(message);
          return;
        }
      }

      setFormData((prev) => ({
        ...(prev || {}),
        ...uploadedData,
        formId,
        form: uploadedForm,
        formName: uploadedForm.label || selectedFile.name,
        fileName: formId ? `${formId}.pdf` : selectedFile.name,
        skipPdfInit: false,
        skipExtractFields: true,
        pdfFile,
        pdfUrl: uploadedData.pdfUrl,
        formFields: uploadedFields,
        status: uploadedForm.status || uploadedData.status || "",
        editLock: acquiredLock,
        updated_settings: {
          ...(prev?.updated_settings || {}),
          signatures: uploadedForm.signatures,
        },
        guessedFormDetails: {
          ...(prev?.guessedFormDetails || {}),
          ...formDetails,
        },
        finalizedUploadPayload: uploadedData || payload,
      }));
      setDetailsModalOpen(false);
      setDetailsModalStep("processing");
      setUploadError("");
      onSuccess(`Form details are ready for ${selectedFile.name}`);
    } catch (error) {
      console.error("Form upload error:", error);
      setUploadError(
        error?.response?.data?.message ||
          "Form upload failed. Please review the form details and try again.",
      );
    } finally {
      setFinalizingUpload(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          Digitize New Form
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a PDF form to start processing and continue in the editor.
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={Boolean(selectedFile) || uploading}
          >
            Select Form
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf"
              onChange={handleFileSelect}
            />
          </Button>
        </Box>
      </Paper>

      <PDFFormEditor
        formData={formData}
        onClose={handlePdfEditorClose}
        onSave={handlePdfEditorSave}
        onNotify={(severity, message) => {
          if (severity === "error") {
            onError(message);
            return;
          }
          onSuccess(message);
        }}
        isOpen={pdfEditorOpen}
        loading={loading}
        isNew={true}
        onError={onError}
        onSuccess={onSuccess}
        setFormData={setFormData}
        jurisdictionOptions={jurisdictionOptions}
        onRecoverEditLock={recoverEditLock}
      />

      <Dialog
        open={detailsModalOpen}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: "92vw", sm: 520 },
            maxWidth: 520,
            minHeight: detailsModalStep === "processing" ? 220 : 420,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {detailsModalStep === "processing" ? (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Processing form...
            </Typography>
            <CircularProgress sx={{ mb: 2 }} />
            <Box sx={{ px: 2, mt: 1, minHeight: 56 }}>
              <Fade in={messageVisible} timeout={250}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    minHeight: 24,
                  }}
                >
                  {PROCESSING_MESSAGES[processingMessageIndex]}
                </Typography>
              </Fade>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isProcessingDelayed
                  ? "Taking a bit longer than expected. Still processing your PDF..."
                  : "This may take a few moments depending on the PDF size and layout."}
              </Typography>
            </Box>

            <Box sx={{ mt: 3, px: 2 }}>
              <LinearProgress
                variant="determinate"
                value={simulatedProgress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "rgba(25, 118, 210, 0.12)",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.25 }}
              >
                Processing progress • {Math.round(simulatedProgress)}%
              </Typography>
            </Box>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                color="error"
                variant="text"
                onClick={handleRemoveSelectedFile}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="h6"
              color="primary"
              sx={{ mb: 2, textAlign: "center" }}
            >
              Verify form details
            </Typography>
            <FormDetailsCard
              selectedFile={selectedFile}
              uploading={finalizingUpload}
              jurisdictionOptions={jurisdictionOptions}
              initialValues={guessedFormDetails || undefined}
              onSubmit={handleUpload}
              onCancel={handleRemoveSelectedFile}
              submitError={uploadError}
              onValuesChange={handleVerifiedDetailsChange}
            />
          </Box>
        )}
      </Dialog>

      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure? All changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>
            No, keep editing
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDiscardConfirmed}
          >
            Yes, discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewForm;
